import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import swal from "sweetalert"
import Loader from "../Common/Loader"
import { useDispatch, useSelector } from "react-redux"
import { getContentListAction, deleteContentAction } from "../../../store/actions/ContentBankAction"

const ContentBank = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { contentList, num_rows: numRows } = useSelector((state) => state.contentBank);

  const [loading, setLoading] = useState(false)
  const [searchQueries, setSearchQueries] = useState({
    title: "",
    type: "",
    disease: "",
  })

  const [sortField, setSortField] = useState("content_bank_id")

  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")
  const rowsPerPage = 10

  const fetchContentFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(
        getContentListAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (err) {
      console.error("Failed to fetch content list:", err);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchContentFromServer()
      .finally(() => setLoading(false));
  }, [dispatch]);

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set the current field
    const clearedQueries = {
      title: "",
      type: "",
      disease: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Backend expects content_title, content_type, etc.
    const searchFieldMap = {
      title: "content_title",
      type: "content_type",
      disease: "disease_name"
    };

    const backendField = searchFieldMap[field] || field;
    setActiveSearchField(backendField);
    setActiveSearchKeyword(keyword);
    
    fetchContentFromServer(backendField, keyword, sortField, sortOrder, true, 0);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    setSortField(field)
    setSortOrder(order)
    setCurrentPage(0)
    
    fetchContentFromServer(
      activeSearchField,
      activeSearchKeyword,
      field,
      order,
      true,
      0
    );
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchContentFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page
    );
  };

  const handleEdit = (item) => {
    navigate("/add-content", {
      state: {
        item: {
          ...item,
          diseases: item.diseases || [], // ✅ ensures diseases are passed
        },
      },
    });
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this content!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteContentAction(id));
          // Refresh the current page after deletion
          await fetchContentFromServer(
            activeSearchField,
            activeSearchKeyword,
            sortField,
            sortOrder,
            true,
            currentPage
          );
        } catch (err) {
          console.error("Failed to delete:", err);
        } finally {
          setLoading(false);
        }
      }
    })
  }

  // Server already returns paginated data
  const paginatedData = contentList;
  const totalPages = Math.ceil(numRows / rowsPerPage);
  const startEntry = numRows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, numRows);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-content" className="btn btn-primary btn-rounded">
            + Add Content
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("content_title")} style={{ cursor: "pointer" }}>
                            Title {sortField === "content_title" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.title}
                              onChange={(e) => handleSearch(e, "title")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Title"
                            />
                          </th>
                          <th onClick={() => handleSort("content_type")} style={{ cursor: "pointer" }}>
                            Type {sortField === "content_type" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.type}
                              onChange={(e) => handleSearch(e, "type")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Type"
                            />
                          </th>
                          {/* <th onClick={() => handleSort("disease_name")} style={{ cursor: "pointer" }}>
                            Disease {sortField === "disease_name" && (sortOrder === "asc" ? "↑" : "↓")} */}
                            <th>
                            Disease
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.disease}
                              onChange={(e) => handleSearch(e, "disease")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Disease"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item) => (
                          <tr key={item.content_bank_id || item.content_id || item.id}>
                            <td>{item.content_title}</td>
                            <td>{item.content_type}</td>
                            <td>
                              {Array.isArray(item.diseases) && item.diseases.length > 0
                                ? item.diseases.map((d) => d.disease_name).join(", ")
                                : "-"}
                            </td>
                            <td>
                              <div className="d-flex">
                                <button
                                  className="btn btn-link p-0 me-3"
                                  onClick={() => handleEdit(item)}
                                  title="Edit Content"
                                >
                                  <i className="fa fa-pencil-alt text-success fs-18"></i>
                                </button>
                                <button
                                  className="btn btn-link p-0"
                                  onClick={() => handleDelete(item.content_bank_id || item.content_id || item.id)}
                                  title="Delete Content"
                                >
                                  <i className="fa fa-trash text-danger fs-18"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No content found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {startEntry} to {endEntry} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ContentBank;
